import './item-edit-screen.scss'

import { faArrowLeft, faCheck, faEdit } from '@fortawesome/free-solid-svg-icons';
import * as _ from 'lodash'
import { combineLatest, of } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import { Item, ItemDataStore, itemSelectors, manifestationSelectors, Wemi, WemiUtils, workSelectors } from '@skinsoft/entity';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { Author } from '@skinsoft/ui/src/item-card/description/author';
import { Media } from '@skinsoft/ui/src/item-card/media';

import { RecordMaskDisplay } from '@skinsoft/record-mask';
import { ThesaurusTermView } from '@skinsoft/ui';
import { CLOSE_ITEM_SCREEN_TOPIC, DELETE_MODIFICATIONS_NOT_SYNCHRONIZED, DELETE_NOT_SYNCHRONIZED_NEW_ITEM, DISPLAY_ALERT_TOPIC, OPEN_AUDIT_CAMPAIGN_POP_UP, SAVE_CURRENT_ITEM_TOPIC } from '../../../busTopics';
import { wemi_metaRecordMask } from '../../../local-record-masks/wemi-meta-record-mask';
import { AuditCampaignPopUp } from './audit-campaign-pop-up';
import { ItemCreationImagePopup } from './image-popup';
import { OfflineDataFetcher } from '@skinsoft/data-store';

@Component({
    components: {
        auditCampaignPopUp: AuditCampaignPopUp,
        itemCreationImagePopup: ItemCreationImagePopup,
        sAuthor: Author,
        sMedia: Media,
        sRecordMaskDisplay: RecordMaskDisplay,
        sThesaurusTermView: ThesaurusTermView
    },
    props: ['item', 'headerTitle', 'induceUpdate'],
    template: require('./item-edit-screen.html')
})
export class ItemEditScreen extends ReactiveVue {
    public item: Item;
    public headerTitle: string;
    private $mediaTransferHelper: MediaTransferHelper;
    private $itemDataStore: ItemDataStore;
    private imageChoice: boolean = false;

    private forceReadonly: boolean = true;
    private wemi: Wemi = null as any;

    private recordMask = wemi_metaRecordMask;

    private induceUpdate: boolean
    private auditCampaignPopUp = false

    // Icons 
    private backIcon = faArrowLeft;

    public mounted() {
        this.subscribeToBus(SAVE_CURRENT_ITEM_TOPIC, this.saveItem);
        this.subscribeToBus(DELETE_NOT_SYNCHRONIZED_NEW_ITEM, this.deleteItem);
        this.subscribeToBus(DELETE_MODIFICATIONS_NOT_SYNCHRONIZED, this.deleteModifications);
        this.subscribeToBus(OPEN_AUDIT_CAMPAIGN_POP_UP, this.toggleAuditCampaignPopUp)
        this.imageChoice = this.$route.query.imageChoice === 'true'
    }

    public beforeDestroy() {
        super.beforeDestroy()
    }

    @Watch('item', { immediate: true })
    public onItemChange() {
        if (this.item) {
            if (window.navigator.onLine) {
                this.subscribeTo(WemiUtils.getWemiFromItem(this.item.id).pipe(first()),
                    wemi => this.wemi = wemi,
                    'wemi-subscription')
            } else {
                this.subscribeTo(WemiUtils.getWemiFromItemOffline(this.item.id).pipe(first()),
                    wemi =>  this.wemi = wemi,
                    'wemi-subscription')
            }
        }
    }

    @Watch('induceUpdate', { immediate: true })
    public onUpdateInduced() {
        if (this.induceUpdate) {
            this.saveWemi();
            this.$emit('update-done')
        }
    }

    private unsubscribe() {
        this.$off(SAVE_CURRENT_ITEM_TOPIC)
        this.$off(DELETE_NOT_SYNCHRONIZED_NEW_ITEM)
        this.$off(DELETE_MODIFICATIONS_NOT_SYNCHRONIZED)
    }

    private onMainButtonClick() {
        this.forceReadonly = !this.forceReadonly;
        if(this.forceReadonly){
            this.saveItem();
        }
    }

    private saveItem() {
        if(_.get(this.item,'associatedMedias.medias') == null){
            _.set(this.item,'associatedMedias.medias', [])
        }
        _.set(this.wemi, 'item', this.$itemDataStore.recursiveGenericMerge(this.wemi.item, this.item))
        combineLatest([..._.get(this.item,'associatedMedias.medias', []).filter(e => e.startsWith('local')).map( e => {
            if(window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(this.wemi.item!.id)){
                return this.$mediaTransferHelper.downloadMedia(e)
                    .pipe(
                        filter(e => e.newMediaId)
                    )
            }
            return of(e)
        })]).subscribe( () => {
                if (this.forceReadonly) {
                    //  TODO save item
                    this.saveWemi();
                }
            }, 
            () => {/**/},
            () => {
                // in case no medias are present
                if (this.forceReadonly) {
                    this.saveWemi()
                }
            }
        )
    }

    private async deleteItem() {
        await this.deleteWemi();
        this.closeItemScreen();
    }

    private deleteModifications() {
        this.deleteModificationsWemi();
        this.onItemChange();
    }

    private saveWemi() {
        WemiUtils.removeEmptyObjectsFromWemi(this.wemi)
        this.$itemDataStore.recursiveGenericMerge(this.wemi.item, this.item)
        if (window.navigator.onLine && !this.item.id.startsWith('_')) {
            WemiUtils.saveWemi(this.wemi).then(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.saveNotification.successMessage'),
                        variant: "success"
                    });
                }
            )
            .catch(
                (error) => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.saveNotification.errorMessage'),
                        variant: "danger"
                    });
                }
            );
        } else {
            WemiUtils.saveWemiOffline(this.wemi).then(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.saveNotification.successMessage'),
                        variant: "success"
                    });
                }
            )
                .catch(
                    (error) => {
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: this.$t('editItemScreen.saveNotification.errorMessage'),
                            variant: "danger"
                        });
                    }
                );
        }
    }

    private deleteWemi() {
        return WemiUtils.deleteWemi(this.wemi).then(
            () => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteNotification.successMessage'),
                    variant: "success"
                });
            }
        )
        .catch(
            (error) => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteNotification.errorMessage'),
                    variant: "danger"
                });
            }
        );
    }

    private deleteModificationsWemi() {
        WemiUtils.deleteModificationsWemi(this.wemi).then(
            () => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteModificationsNotification.successMessage'),
                    variant: "success"
                });
            }
        )
        .catch(
            (error) => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteModificationsNotification.errorMessage'),
                    variant: "danger"
                });
            }
        );
    }

    private onValueChange(event: any) {
        // update the field of wemi with event data
        _.set(this.wemi, `${event.subWemi}.${event.fieldPath}`, event.value);
        this.wemi = Object.assign({}, this.wemi);
    }

    private closeItemScreen() {
        this.$emit(CLOSE_ITEM_SCREEN_TOPIC)
    }

    // Button parameters
    private get buttonLabel() {
        return this.forceReadonly ?
            "Modifier" : "Valider"
    }

    private get buttonIcon() {
        return this.forceReadonly ?
            faEdit : faCheck
    }
    private get buttonColor() {
        return this.forceReadonly ?
            "" : "validate-color"
    }

    private get mediaId() {
        return itemSelectors.media(this.item);
    }

    private get title() {
        if (this.wemi.work) {
            return workSelectors.title(this.wemi.work, this.$i18n.locale)
        }
    }

    private get inventoryNumber() {
        if (this.wemi.item) {
            return itemSelectors.identifier(this.wemi.item, this.$i18n.locale)
        }
    }

    private get type() {
        if (this.wemi.item) {
            return itemSelectors.type(this.item)
        }
    }

    private get authorId() {
        if (this.wemi.work) {
            return workSelectors.credits(this.wemi.work).alias
        }
    }

    private get workType() {
        if (this.wemi.work) {
            return workSelectors.workType(this.wemi.work)
        }
    }

    private get domain() {
        if (this.wemi.manifestation && !_.isEmpty(manifestationSelectors.domain(this.wemi.manifestation))) {
            return manifestationSelectors.domain(this.wemi.manifestation);
        }
    }

    private get denomination() {
        if (this.wemi.manifestation && !_.isEmpty(manifestationSelectors.name(this.wemi.manifestation))) {
            return manifestationSelectors.name(this.wemi.manifestation);
        }

    }

    private closePopup() {
        this.imageChoice = false
    }

    private applyImage(image) {
        this.closePopup()
        if (!this.item.associatedMedias) {
            this.$set(this.item, 'associatedMedias', {
                medias: []
            })
        }
        this.addImage(image);
    }

    private addImage(image: any) {
        this.$set(this.item.associatedMedias, 'medias', _.uniq(_.concat(this.item.associatedMedias.medias, [image])));
        this.$set(this.item.associatedMedias, 'first', image);
        if (window.navigator.onLine) {
            this.$mediaTransferHelper.downloadMedia(image).subscribe(media => {
                if (media.newMediaId) {
                    this.$itemDataStore.addImage(this.item, media)
                }
            })
        } else {
            this.saveItem()
        }
    }

    private onAddMedia(media){
        if(!_.get(this.item, 'associatedMedias.medias', []).includes(media.mediaId)) {
            if(_.get(this.item, 'associatedMedias.medias') === null){
                _.set(this.item, 'associatedMedias.medias', [media.mediaId]);
            }else{
                _.get(this.item, 'associatedMedias.medias').push(media.mediaId);
            }
        }

        this.$itemDataStore.recursiveGenericMerge(this.wemi.item, this.item)
    }

    private get isOfflineItem() {
        return !window.navigator.onLine || this.item.id.startsWith('_')
    }

    private toggleAuditCampaignPopUp() {
        this.auditCampaignPopUp = !this.auditCampaignPopUp
    }
}